import React, { useState, useEffect, useMemo } from 'react';
import { PrimaryButton, SecondaryButton } from "../UI";
import ConfigureExperience from '../Experience/ConfigureExperience';
import ChooseTemplate from '../Template/ChooseTemplate';
import tickImg from '../../assets/tick-step.svg';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { createContentfulEntry, createExperience, createTenantActivityOffers, delinkOfferFromExperience, editExperience, editExperienceOfferMapping, getAEMFragmentJsonPath, getContentfulEntries, getOfferExperienceMappings, updateContentfulEntry, getContentTypes } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../UI/Loader/Loader';
import { parsePath, useNavigate, useParams } from 'react-router-dom';
import { compareJson, getNestedData, isValidJsonString } from '../../utils/helper';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import Input from '../UI/Input';
import { permission } from '../../config/permission';
import OfferCode from '../Offers/offerCode';
import DropdownButton from './DropdownButton';
import CreateOfferAndExperienceModal from '../ManageAEMContentFragments/CreateOfferAndExperienceModal';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import Activation from '../ExperienceCode/Activation';
import EditJsonPathModal from '../ManageAEMContentFragments/EditJsonPathModal';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import UpdateEntryAssociationModal from './UpdateEntryAssociationModal';
import { resolveUploadFields, hasUploadFields } from '../../utils/uploadHelper';

const ContentfulEntryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enums, content: contentfulData } = useContentfulContext();
  const { enablePreviewMode, disablePreviewMode } = usePreviewMode();

  const [currentStep, setCurrentStep] = useState('choose-template');
  const [isStepperHidden, setIsStepperHidden] = useState(false);
  const [contentfulEntryName, setContentfulEntryName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { userData, checkPermission } = useUserContext();
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [fieldValues, setFieldValues] = useState();
  const [transformedExperienceJSON, setTransformedExperienceJSON] = useState();
  const [contentfulEntry, setContentfulEntry] = useState()
  const [fullEntryData, setFullEntryData] = useState(null);
  const [originalContentfulEntry, setOriginalContentfulEntry] = useState();
  const [validationError, setValidationError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState();
  const [type, setType] = useState('');
  const [jsonPathModal, setJsonPathModal] = useState({ isOpen: false, type: null, contentfulEntry: null });
  const [experience, setExperience] = useState(null);
  const [fieldKey, setFieldKey] = useState(null);
  const [contentTypes, setContentTypes] = useState([]);
  const [contentTypeJson, setContentTypeJson] = useState(null);

  const [showUpdateOfferModal, setShowUpdateOfferModal] = useState(false);
  const [associatedExperiences, setAssociatedExperiences] = useState([]);
  const [hasNoTemplates, setHasNoTemplates] = useState(false);

  const currentExperienceJSON = useMemo(() => {
    if (hasNoTemplates) {
      return isValidJsonString(contentfulEntry) ? JSON.parse(contentfulEntry) : contentfulEntry;
    }
    return transformedExperienceJSON;
  }, [hasNoTemplates, contentfulEntry, transformedExperienceJSON]);

  useEffect(() => {
    getContentfulEntry()
    getEntryExpMappings()
    fetchContentTypes()
  }, [])

  const fetchContentTypes = async () => {
    try {
      const result = await getContentTypes({
        token: userData?.token,
        organizationId: userData?.selectedOrganization?.org_token,
        contentful_id: userData?.defaultContentful?._id,
      });
      if (result.status) {
        setContentTypes(result.data.items || []);
      }
    } catch (err) {
      console.error('Error fetching content types:', err);
    }
  };

  async function getEntryExpMappings() {
    try {
      const mappings = await getOfferExperienceMappings(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        id,
        userData?.defaultContentful?._id
      );
      setAssociatedExperiences(mappings?.data || []);
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setError('');
      setSuccessMessage('');
    }, 4000);

    return () => clearTimeout(timer);
  }, [error, successMessage]);

  const jsonPathModalClose = () => {
    // If the state hasn't been updated or user hasn't made changes
    if (!contentfulEntry || !originalContentfulEntry) {
      navigate("/experience/contentful-entries");
    } else {
      setJsonPathModal({
        isOpen: false,
        type: "",
        contentfulEntry: null
      });
    }
  };

  function isFieldsJson(fields) {
    // If there is only **one key**, and that key's value is an object (not array/null)
    const keys = Object.keys(fields);

    if (keys.length === 1) {
      const value = fields[keys[0]];
      return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0
      );
    }

    return false; // More than 1 field => not JSON
  }

  // Check if the fields object itself (multi-key) is a usable plain JSON object
  function isFieldsDirectlyJson(fields) {
    return (
      typeof fields === 'object' &&
      fields !== null &&
      !Array.isArray(fields) &&
      Object.keys(fields).length > 0
    );
  }

  const updateContentfulContentFromPath = (jsonPath, fullData) => {
    let pathData = getNestedData(fullData, jsonPath);

    const keys = Object.keys(pathData || {})
    if (keys.length === 1 && typeof pathData[keys[0]] === "object" && pathData[keys[0]] !== null) {
      pathData = pathData[keys[0]]
      setFieldKey(keys[0])
    }
    const isJson = isFieldsJson(fullData?.fields)

    if (pathData && isJson) {
      // Primary path: single key whose value is a JSON object (e.g. vovDetails, heroSection)
      const temp = pathData;
      const name = fullData.sys.id || "";
      setContentfulEntry(JSON.stringify(temp))
      setOriginalContentfulEntry(JSON.stringify(temp))
      setType("json")
      setContentfulEntryName(name)
      setFullEntryData(fullData)
      return true;
    } else if (!isJson && isFieldsDirectlyJson(fullData?.fields)) {
      // Fallback: single key value is NOT a JSON object, but fields itself is a usable JSON
      // e.g. fields = { headline: "text", subheadline: "text", ctaText: "text" }
      const temp = fullData.fields;
      const name = fullData.sys.id || "";
      setFieldKey(null); // no wrapping key, fields is used directly
      setContentfulEntry(JSON.stringify(temp))
      setOriginalContentfulEntry(JSON.stringify(temp))
      setType("json")
      setContentfulEntryName(name)
      setFullEntryData(fullData)
      return true;
    } else {
      if (pathData && !isJson) {
        toast.error("Selected fragment is not of type JSON");
        navigate("experience/contentful-entries");
        return;
      } else {
        return null;
      }
    }
  };

  const getContentfulEntry = async () => {
    try {
      if (!userData?.defaultContentful) {
        toast.error(contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.no_contentful_entry_found);
        return;
      }
      setLoading(true);
      const result = await getContentfulEntries({
        token: userData?.token,
        organizationId: userData?.selectedOrganization?.org_token,
        contentful_id: userData?.defaultContentful?._id,
        id: id
      });
      const entry = removeLocalesFromEntry(result?.data)
      const elements = entry?.fields || {};
      const elementKeys = Object.keys(elements);

      let resultFragments;
      // step 1: check with default path
      if (elementKeys && elementKeys.length) {
        resultFragments = updateContentfulContentFromPath(`fields`, entry)
      } else {
        resultFragments = null;
      }

      if (!resultFragments) {
        //step 2: check path from DB
        const contentfulJsonPath = await getAEMFragmentJsonPath(
          userData.token,
          userData.selectedOrganization.org_token,
          userData.defaultAEMAdobeIO._id
        );

        if (contentfulJsonPath.status && contentfulJsonPath?.data) {

          // scenario: when path mapping exist for give content fragment
          const jsonPath = contentfulJsonPath.data?.json_path;

          const response = updateContentfulContentFromPath(jsonPath, entry)
          if (!response) {
            // if json is not present on the available  mapping path
            setError(contentfulData.AEM_CONTENT_DETAILS.errors.get_saved_path);
            setJsonPathModal({
              isOpen: true,
              type: "edit",
              contentfulEntry: entry
            });
          }
        } else {
          // when the path does not exist in the db
          setJsonPathModal({
            isOpen: true,
            type: "add",
            contentfulEntry: entry
          });
        }
      }

    } catch (error) {
      toast.error(error?.message || contentfulData?.COMMON_MESSAGE.something_wrong_msg)
    } finally {
      setLoading(false);
    }
  }

  function removeLocalesFromEntry(entry) {
    const stripLocaleFromFields = (fields) => {
      const cleaned = {};
      for (const key in fields) {
        if (typeof fields[key] === 'object' && fields[key] !== null) {
          const localeKeys = Object.keys(fields[key]);
          if (localeKeys.length === 1 && /^[a-z]{2}-[A-Z]{2}$/.test(localeKeys[0])) {
            // Likely a locale key like "en-US"
            cleaned[key] = fields[key][localeKeys[0]];
          } else {
            // Nested object, recurse
            cleaned[key] = stripLocaleFromFields(fields[key]);
          }
        } else {
          cleaned[key] = fields[key];
        }
      }
      return cleaned;
    };

    const cleanedEntry = {
      ...entry,
      fields: stripLocaleFromFields(entry.fields),
    };
    return cleanedEntry;
  }

  const handleUpdatecontentfulEntry = async (showLoader = true) => {
    try {
      if (!isValidJsonString(contentfulEntry)) {
        setError("Invalid JSON in contentful entry.");
        return;
      }

      let newParsedData = (showPreview && selectedTemplate?._id) ? transformOfferJSON() : JSON.parse(contentfulEntry);
      if (selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id && typeof newParsedData === 'string') {
        newParsedData = { content: newParsedData };
      }
      const version = fullEntryData?.sys?.version;
      const contentTypeId = fullEntryData?.sys?.contentType?.sys?.id;

      function detectLocalesFromFieldStatus(data) {
        const fieldStatus = data.sys.fieldStatus;
        if (!fieldStatus || typeof fieldStatus !== 'object') return [];

        const wildcardStatus = fieldStatus["*"];
        if (!wildcardStatus || typeof wildcardStatus !== 'object') return [];

        return Object.keys(wildcardStatus)[0];
      }

      // const locale = detectLocalesFromFieldStatus(fullEntryData)
      const locale = "en-US"

      if (!version) {
        toast.error("Missing version info.");
        return;
      }

      // Prepare the structure that goes inside 'fields'
      const contentToSend = fieldKey
        ? { [fieldKey]: newParsedData } // Wrap inside the original single field key
        : newParsedData;

      const updatedFields = {};

      for (const key of Object.keys(contentToSend)) {
        const value = contentToSend[key];
        updatedFields[key] = {
          [locale]: value
        };
      }

      const updatedEntry = {
        fields: updatedFields
      };

      if (showLoader) setLoading(true);
      const result = await updateContentfulEntry({
        token: userData?.token,
        organizationId: userData?.selectedOrganization?.org_token,
        entry_id: contentfulEntryName,
        fieldsData: updatedEntry,
        version,
        content_type: contentTypeId,
        contentful_id: userData?.defaultContentful?._id,
      });
      if (result.status) {
        toast.success("Contentful entry updated successfully.");
        setOriginalContentfulEntry(JSON.stringify(newParsedData));
        const entry = removeLocalesFromEntry(result?.data)
        setFullEntryData(entry);

        if (contentTypes.length > 0 && entry?.sys?.contentType?.sys?.id) {
          const matchingType = contentTypes.find(t => t.sys.id === entry.sys.contentType.sys.id);
          if (matchingType) {
            // Keep as array of field IDs — consistent with the contentTypes useEffect below
            const fieldIds = (matchingType.fields || []).map(f => f.id);
            setContentTypeJson(fieldIds);
          }
        }
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update entry.");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  function getFlattenExperienceJSON(ob) {
    let result = {};
    function recurse(cur, prop) {
      if (Object(cur) !== cur) {
        result[prop] = cur;
      } else if (Array.isArray(cur)) {
        for (let i = 0; i < cur.length; i++) {
          recurse(cur[i], prop ? prop + "[" + i + "]" : i);
        }
        if (cur.length === 0) result[prop] = [];
      } else {
        let isEmpty = true;
        for (let p in cur) {
          isEmpty = false;
          recurse(cur[p], prop ? prop + "." + p : p);
        }
        if (isEmpty && prop) result[prop] = {};
      }
    }
    recurse(ob, "");
    return result;
  }

  const createOffer = async (values) => {
    try {
      if (!contentfulEntryName) {
        setError(contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.name_required)
        return;
      }
      if (validationError) return;

      if (showPreview) {
        const isValidate = handleValidateJson();
        if (isValidate.length > 0) return;
      }

      if (!isValidJsonString(contentfulEntry)) {
        setError(contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT?.errors?.invalid_json || "Invalid JSON in entry");
        return;
      }
      const content = JSON.parse(contentfulEntry)

      const payload = {
        tenant: userData.defaultAdobeIO?.tenant_name,
        data: {
          name: values.offer_name,
          workspace: userData.defaultWorkspace?.workspace_id,
          content: (showPreview && selectedTemplate?._id) ? transformOfferJSON() : content,
          type: selectedTemplate?.type === enums.templateTypes.json_a_to_html.id ? 'content' : 'json',
        }
      }
      const result = await createTenantActivityOffers(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload
      )
      return result;
    } catch (error) {
      throw error;
    }
  };

  const createExperienceHandler = async (values, offerId = id) => {
    try {
      if (!selectedTemplate) {
        setError(contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.template_required);
        return;
      }

      if (showPreview) {
        const isValidate = handleValidateJson();
        if (isValidate.length > 0) {
          setError(isValidate.join(", \n"))
          return
        }
      }

      if (validationError) {
        setError(validationError);
        return
      }

      if (!isValidJsonString(contentfulEntry)) {
        setError("Invalid JSON in entry");
        return;
      }
      const content = JSON.parse(contentfulEntry)

      const payload = {
        name: values.experience_name,
        templates: [selectedTemplate._id],
        html: '',
        css: '',
        config_json: '',
        experience_json: (showPreview && selectedTemplate?._id) ? JSON.stringify(transformOfferJSON()) : contentfulEntry,
        source: enums.experience_sources.offer_page.id,
        ...(offerId && { do_publish: true })   // flag will publish the created version
      }
      const result = await createExperience(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload
      );

      if (result?.data?.length > 0 && offerId) {
        const mappingPayload = {
          experience_id: result.data[0]?._id,
          id: offerId,
          integration_id: userData?.defaultContentful?._id,
          destination: "contentful"
        };
        await editExperienceOfferMapping(
          userData?.token,
          userData?.selectedOrganization?.org_token,
          mappingPayload
        );
      }
      setExperience(result?.data)
      return result;
    } catch (error) {
      throw error;
    }
  }

  const handleSaveAsNewEntry = async (contentType) => {
    try {
      setLoading(true);

      const locale = "en-US";
      const rawCode = (showPreview && selectedTemplate?._id) ? transformOfferJSON() : JSON.parse(contentfulEntry);
      const codeValue = selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id
        ? (typeof rawCode === 'string' ? { content: rawCode } : rawCode)
        : (typeof rawCode === "string" && isValidJsonString(rawCode) ? JSON.parse(rawCode) : rawCode);

      let fields;
      if (contentType.isSingleField && contentType.fieldKey) {
        // Single-field JSON content type: wrap entire content under the one field key
        fields = {
          [contentType.fieldKey]: { [locale]: codeValue }
        };
      } else {
        // Multi-field content type: each key in the content object maps to its own field
        const contentObj = (typeof codeValue === 'object' && codeValue !== null) ? codeValue : {};
        fields = {};
        for (const key of Object.keys(contentObj)) {
          fields[key] = { [locale]: contentObj[key] };
        }
      }

      const payload = {
        token: userData?.token,
        content_type: contentType.id,
        organizationId: userData?.selectedOrganization?.org_token,
        fieldsData: { fields },
        contentful_id: userData?.defaultContentful?._id,
        experience_id: experience?._id
      }
      const result = await createContentfulEntry(payload)

      // Throw error if creation failed
      if (!result?.status) {
        throw new Error(result?.message || "Failed to create new offer");
      }

      return result; // return for caller if needed

    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAssociatedExperiences = async () => {
    if (associatedExperiences.length <= 0) {
      toast.error("No associated experiences to update.");
      return;
    }
    try {
      setLoading(true);
      await Promise.all(
        associatedExperiences.map(exp => {
          const payload = {
            name: exp.name,
            templates: [selectedTemplate._id],
            html: '',
            css: '',
            config_json: '',
            experience_json: (showPreview && selectedTemplate?._id) ? JSON.stringify(transformOfferJSON()) : contentfulEntry,
            version: exp.published_version  // for updating published version which can update all associated activations
          };
          return editExperience(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            exp.experience_id,
            userData?.defaultAdobeIO?._id,
            userData?.defaultContentful?._id,
            userData?.defaultTealiumKey?._id,
            payload
          );
        })
      );
      toast.success("Associated experiences updated successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update associated experiences");
    } finally {
      setLoading(false);
    }
  };

  const handleDelinkExperience = async () => {
    if (associatedExperiences.length <= 0) {
      toast.error("No associated experiences to delink.");
      return;
    }
    try {
      setLoading(true);
      await Promise.all(
        associatedExperiences.map(exp =>
          delinkOfferFromExperience(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            {
              experience_id: exp.experience_id,
              id: id,
              integration_id: userData?.defaultContentful?._id,
              destination: "contentful",
            }
          )
        )
      );
      setAssociatedExperiences([]);
      await handleUpdatecontentfulEntry(false)
      toast.success("Offer delinked from associated experiences");
    } catch (err) {
      toast.error(err?.message || "Failed to delink offer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contentTypes.length > 0 && fullEntryData?.sys?.contentType?.sys?.id) {
      const matchingType = contentTypes.find(t => t.sys.id === fullEntryData.sys.contentType.sys.id);
      if (matchingType) {
        // Array of field IDs: used to determine if this is a multi-field entry
        // and to match template top-level keys against content type field names
        const fieldIds = (matchingType.fields || []).map(f => f.id);
        setContentTypeJson(fieldIds);
      }
    }
  }, [contentTypes, fullEntryData]);

  useEffect(() => {
    if (currentStep == 'choose-template' && selectedTemplate._id) {
      if (!isValidJsonString(selectedTemplate?.json_schema)) {
        setError(contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.valid_json)
        return;
      }
      const AEMJson = JSON.parse(contentfulEntry)

      let isCompatible = true;
      // When fieldKey is set, it's a single-field JSON entry. The contentTypeJson array only
      // contains the wrapper field ID (e.g. ["vovDetails"]), NOT the inner keys the template
      // schema references. So always compare against the actual JSON content in that case.
      if (!fieldKey && contentTypeJson && contentTypeJson.length > 0) {
        // contentTypeJson is an array of field IDs from the content type.
        // Exact match: template top-level keys must be exactly the same set as the field IDs.
        const templateTopLevelKeys = Object.keys(JSON.parse(selectedTemplate.json_schema));
        isCompatible = (
          templateTopLevelKeys.length === contentTypeJson.length &&
          templateTopLevelKeys.every(key => contentTypeJson.includes(key))
        );
      } else {
        const compare = compareJson(JSON.parse(selectedTemplate?.json_schema), AEMJson);
        isCompatible = (compare.length == 0) || (compare.length == 1 && compare.includes("experience_id"));
      }

      if (isCompatible) {
        setFieldValues(getFlattenExperienceJSON(AEMJson));
      } else {
        setError(`${contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.templates_not_same}`)
        return;
      }
    }
    if (selectedTemplate?._id && currentStep == 'choose-template') {
      setCurrentStep('configure-offer');
    }
  }, [selectedTemplate, contentTypeJson])

  const handleNext = () => {
    if (currentStep == 'choose-template' && !selectedTemplate?._id && !hasNoTemplates) {
      setError(contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.template_required);
      return;
    }
    if (currentStep == 'choose-template' && selectedTemplate._id) {
      if (!isValidJsonString(selectedTemplate?.json_schema)) {
        setError(contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.valid_json)
        return;
      }
      const AEMJson = JSON.parse(contentfulEntry)

      let isCompatible = true;
      // When fieldKey is set, it's a single-field JSON entry: skip field-ID array comparison
      if (!fieldKey && contentTypeJson && contentTypeJson.length > 0) {
        // Exact match: template top-level keys must be exactly the same set as the field IDs
        const templateTopLevelKeys = Object.keys(JSON.parse(selectedTemplate.json_schema));
        isCompatible = (
          templateTopLevelKeys.length === contentTypeJson.length &&
          templateTopLevelKeys.every(key => contentTypeJson.includes(key))
        );
      } else {
        const compare = compareJson(JSON.parse(selectedTemplate?.json_schema), AEMJson);
        isCompatible = compare.length == 0 || (compare.length == 1 && compare.includes("experience_id"));
      }

      if (isCompatible) {
        setFieldValues(getFlattenExperienceJSON(AEMJson));
      } else {
        setError(`${contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.errors.templates_not_same}`)
        return;
      }
    }
    if (currentStep == 'choose-template') {
      if (hasNoTemplates && !selectedTemplate?._id) {
        setCurrentStep('update-offer');
      } else {
        setCurrentStep('configure-offer');
      }
    } else if (currentStep == 'configure-offer') {
      setCurrentStep('update-offer');
    } else if (currentStep == 'update-offer') {
      setCurrentStep('activate');
    }
  }

  function transformOfferJSON() {
    const transformedJSON = {};
    for (const key in fieldValues) {
      const keys = key.split('.');
      keys.reduce((acc, part, index) => {
        if (part.includes('[')) {
          const [arrayKey, arrayIndex] = part.split(/\[|\]/).filter(Boolean);
          if (!acc[arrayKey]) {
            acc[arrayKey] = [];
          }
          if (!acc[arrayKey][arrayIndex]) {
            acc[arrayKey][arrayIndex] = {};
          }
          if (index === keys.length - 1) {
            acc[arrayKey][arrayIndex] = fieldValues[key];
          }
          return acc[arrayKey][arrayIndex];
        } else {
          if (index === keys.length - 1) {
            acc[part] = fieldValues[key];
          } else {
            if (!acc[part]) {
              acc[part] = {};
            }
            return acc[part];
          }
        }
      }, transformedJSON);
    }
    return transformedJSON;
  }

  const handleValidateJson = (obj) => {
    setValidationError("");

    if (!selectedTemplate?._id || !selectedTemplate?.config_json) return [];

    const config_elems = [];
    JSON.parse(selectedTemplate.config_json).forEach((el) => {
      el.error = "";
      config_elems.push({ ...el, id: el.id.replace("[", ".").replace("]", "") });
    });
    const newConfigElems = [...config_elems];
    const errs = [];

    for (let item of newConfigElems) {
      const val = fieldValues[item.id];
      const rules = item.rules;
      if (rules.is_mandatory) {
        if (val?.length == 0) {
          const message = "Field is required";
          item.error = message;
          errs.push(`${item.id}: ${message}`)
        } else {
          item.error = "";
        }
      }
      if (rules.min_length && rules.max_length && item.error.length == 0) {
        if (val.length > rules.max_length || val.length < rules.min_length) {
          const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
          item.error = message;
          errs.push(`${item.id}: ${message}`);
        } else {
          item.error = "";
        }
      }
    }

    if (errs.length > 0) {
      setValidationError(errs.join(", \n"));
    }
    return errs;
  }

  const handleSubmit = async (action, values) => {
    try {
      setError('');
      setLoading(true);
      let successMsg, result;
      switch (action) {
        case contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.id:
          if (!values?.offer_name) {
            setError("Offer name is required.")
            return;
          }
          result = await createOffer(values);
          successMsg = `${contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.success}. Offer Id - ${result.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`;
          break;

        case contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.id:
          if (!values?.experience_name) {
            setError("Experience name is required.")
            return;
          }
          result = await createExperienceHandler(values);
          successMsg = contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.success
          break;

        case contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.both.id:
          if (!values?.experience_name || !values?.offer_name) {
            setError("Fields are required.")
            return;
          }
          const offerResult = await createOffer(values);
          result = await createExperienceHandler(values, offerResult?.data?.id);
          successMsg = `${contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.both.success}. Offer Id - ${offerResult.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`;
          break;

        default:
          break;
      }

      if (result) {
        setSuccessMessage(successMsg);
        setTimeout(() => {
          navigate("/experience/contentful-entries")
        }, 1500);
      }
    } catch (error) {
      console.log('error', error)
      setError(error?.message || contentfulData?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (action) => {
    switch (action) {
      case contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.update_contentful.id:
        setShowUpdateOfferModal(true)
        // handleUpdatecontentfulEntry()
        break;
      case contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.id:
        setCurrentStep("activate")
        break;
      case contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.id:
        setShowCreateOfferModal({
          action,
          fields: [contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.id]
        })
        break;
      case contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.both.id:
        setShowCreateOfferModal({
          action,
          fields: [contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.id, contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.id]
        })
        break;
      default:
        console.log(`Unknown action: ${action}`);
    }
  }

  const handleCloseModal = () => {
    setShowCreateOfferModal(null)
  }

  useEffect(() => {
    if (currentStep == "configure-offer") {
      enablePreviewMode();
    } else {
      disablePreviewMode();
    }
  }, [currentStep])

  return (
    <div className='w-full mx-auto overflow-auto sticky z-[15]'>
      {loading && <Loader />}
      {error && <ErrorAlert timeout="3000">{error}</ErrorAlert>}
      {successMessage && <SuccessAlert disappearTime={4000}>{successMessage}</SuccessAlert>}
      {/* Back Link */}
      {!showPreview && currentStep !== 'activate' && (<div className="w-full px-8 pt-4 pb-2">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate("/experience/contentful-entries");
          }}
          className="font-medium text-primary hover:underline flex items-center"
        >
          <img src={backwardArrow} alt="back" className="w-5 h-5 mr-2" />
          {contentfulData?.CONTENTFUL_ENTRY_DETAILS_CONTENT?.back_cta_text2 || "Back to Contentful Entries"}
        </a>
      </div>)}
      <div className='py-2 px-4 md:px-8 bg-gray-1 mb-2 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4'>
        <div className='w-full md:max-w-[260px]'>
          <Input
            size="sm"
            disabled
            id="offer_name"
            name="offer_name"
            value={contentfulEntryName}
            placeholder={contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.contentful_entry_name}
          />
        </div>
        {!showPreview && !isStepperHidden && <div className='flex justify-end gap-3'>
          {checkPermission(permission.AEM_CONTENT_FRAGMENT_OP_CREATE_PREVIEW) && <SecondaryButton size={'base'} handleClick={() => {
            setShowPreview(true)
          }}>{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.show_preview_cta}</SecondaryButton>}
          {
            checkPermission(permission.CONTENTFUL_ENTRY_OP_OFFER) && <PrimaryButton size={'base'}
              handleClick={() => {
                setCurrentStep("activate")
                setIsStepperHidden(true)
              }}
            >
              {contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.name}
            </PrimaryButton>
          }
        </div>}
        {showPreview && <ol className="flex items-center justify-center flex-1 text-xs font-medium text-center text-gray-500 gap-2 md:gap-0">
          <li className="flex md:w-full items-center sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10">
            <button className="text-xs flex items-center flex-col after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200" onClick={() => {
              setContentfulEntry(originalContentfulEntry)
              setCurrentStep("choose-template");
              setSelectedTemplate({})
              setFieldValues()
            }}
            >
              <img alt='' src={tickImg} className="w-4 h-4" />
              <span className="hidden lg:block">{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.choose_template}</span>
            </button>
          </li>
          {!hasNoTemplates && <li className="text-xs flex items-center">
            <button className="flex items-center flex-col" onClick={() => {
              if (currentStep == "update-offer") {
                setCurrentStep("configure-offer")
              }
            }}>
              {(currentStep == 'configure-offer' || currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">2</span>}
              <span className="hidden lg:block">{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.configure}</span>
            </button>
          </li>}
          <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
            <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
              {(currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">{hasNoTemplates ? "2" : "3"}</span>}
              <span className="hidden lg:block">{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.update_contentful}</span>
            </button>
          </li>
          <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
            <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
              {(currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">{hasNoTemplates ? "3" : "4"}</span>}
              <span className="hidden lg:block">{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.activate}</span>
            </button>
          </li>
        </ol>}
        {(showPreview || currentStep === 'activate') && (
          <div className='flex justify-end gap-2 md:gap-3 w-full md:w-auto'>
            {((showPreview && currentStep !== 'activate') || (!showPreview && currentStep === 'activate')) && <SecondaryButton size={'base'} handleClick={() => navigate("/experience/contentful-entries")}>{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.cancel_btn}</SecondaryButton>}
            {showPreview && (currentStep == "update-offer" ? (
              checkPermission(permission.EDIT_OFFER) && <DropdownButton handleSubmit={handleAction} />
            ) : ((currentStep !== "choose-template" || hasNoTemplates) && currentStep !== 'activate') ? (
              <PrimaryButton size={'base'} handleClick={handleNext}>{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.next_btn}</PrimaryButton>
            ) : null)}
          </div>
        )}
      </div>
      <div className='h-full max-h-[calc(100vh-80px)] px-4 md:px-8'>
        {currentStep == 'choose-template' && contentfulEntry && (
          <ChooseTemplate
            title={contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.title}
            offerJson={{
              content: isValidJsonString(contentfulEntry) ? JSON.parse(contentfulEntry) : contentfulEntry,
              // When fieldKey is set, it means the entry has a single field whose value is JSON.
              // In that case the contentTypeJson array only contains that one wrapper field ID (e.g. ["vovDetails"]),
              // which won't match the inner template schema keys. So we pass the actual content for
              // direct JSON comparison instead. For multi-field entries, pass the field-ID array as usual.
              compareContent: fieldKey ? null : contentTypeJson,
              isCreateOffer: true,
              offerType: "json"
            }}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            hideTemplates={!showPreview}
            type={type}
            isContentfulFlow={true}
            onChange={(val) => setContentfulEntry(val)}
            onNoTemplatesFound={(val) => setHasNoTemplates(val)}
          />
        )}
        {currentStep == 'configure-offer' && (
          <ConfigureExperience
            setCurrentStep={setCurrentStep}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            setTransformedExperienceJSON={setTransformedExperienceJSON}
            actionText={contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.edit_contentful_entry}
          />
        )}
        {currentStep == 'update-offer' &&
          <OfferCode
            title={contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.title}
            experienceJSON={currentExperienceJSON}
            selectedTemplate={selectedTemplate}
            offerJson={contentfulEntry}
            onChange={(val) => setContentfulEntry(val)}
            getFlattenExperienceJSON={getFlattenExperienceJSON}
            setFieldValues={setFieldValues}
            validationError={validationError}
            setValidationError={setValidationError}
            handleValidateJson={handleValidateJson}
          />
        }
        {currentStep == 'activate' &&
          <>
            <h1 className="text-xl px-6 py-4 font-extrabold leading-tight tracking-tight text-gray-900 md:text-2xl">{contentfulData.CONTENTFUL_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.name}</h1>
            <Activation
              experience={experience}
              experienceJSON={currentExperienceJSON}
              setExperience={setExperience}
              validationError={validationError}
              setValidationError={setValidationError}
              handleValidateJson={handleValidateJson}
              offerPermission={{
                create: permission.CREATE_EXP_OP_CREATE_OFFER
              }}
              contentfulPermission={{
                create: permission?.CREATE_EXP_OP_CREATE_CONTENTFUL
              }}
              selectedTemplate={isStepperHidden ? null : selectedTemplate}
              code={(showPreview && selectedTemplate?._id) ? transformOfferJSON() : JSON.parse(contentfulEntry)}
              offerActionConfig={{   /* passed config with create offer handler*/
                handler: createOffer, /* call this handle for create offer if passed*/
                source: "contentful"
              }}
            />
          </>
        }
        {showCreateOfferModal && (
          <CreateOfferAndExperienceModal
            error={error}
            title={'Contentful Entry'}
            loading={loading}
            handleSubmit={handleSubmit}
            handleCloseModal={handleCloseModal}
            offerSuccessMessage={successMessage}
            showCreateOfferModal={showCreateOfferModal}
          />
        )}

        {
          jsonPathModal?.isOpen && jsonPathModal?.contentfulEntry && (
            <EditJsonPathModal
              onClose={jsonPathModalClose}
              type={jsonPathModal?.type}
              data={jsonPathModal?.contentfulEntry}
              onSuccess={updateContentfulContentFromPath}
              source="contentful"
            />
          )
        }

        {showUpdateOfferModal && (
          <UpdateEntryAssociationModal
            isOpen={showUpdateOfferModal}
            onClose={() => setShowUpdateOfferModal(false)}
            associatedExperiences={associatedExperiences}
            onSaveAsNewOffer={(contentType) => handleSaveAsNewEntry(contentType)}
            onUpdateEntry={handleUpdatecontentfulEntry}
            onUpdateAssociatedExperience={handleUpdateAssociatedExperiences}
            onDelinkExperience={handleDelinkExperience}
            loading={loading}
            selectedTemplate={selectedTemplate}
            experienceJson={(showPreview && selectedTemplate?._id) ? transformOfferJSON() : (isValidJsonString(contentfulEntry) ? JSON.parse(contentfulEntry) : contentfulEntry)}
          />
        )}
      </div>
    </div>
  )
}

export default ContentfulEntryDetails;